import { useMutation } from '@apollo/client/react'
import { Input } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  type Customer,
  SetTenantMetabaseDashboardIdDocument,
} from '~app/graph/generated/graphql'
import useSectionForm from '~app/hooks/useSectionForm'
import SectionCard from './SectionCard'

/*
 * Component
 */

interface Props {
  customer: Pick<Customer, 'id' | 'metabaseDashboardId'>
  onStateChange: (args: { isDirty: boolean; isSaving: boolean }) => void
}

const ReportingSection = ({ customer, onStateChange }: Props) => {
  const { t } = useTranslation()
  const [setMetabaseDashboardId] = useMutation(
    SetTenantMetabaseDashboardIdDocument
  )

  const { isDirty, isLoading, reset, submit, updateValues, values } =
    useSectionForm({
      errorMessage: t('customerDetails.notifications.saveFailed'),
      onStateChange,
      serverValues: { metabaseDashboardId: customer.metabaseDashboardId ?? '' },
      successMessage: t('customerDetails.notifications.reportingSaved'),
      onSubmit: async ({ values }) => {
        const { data } = await setMetabaseDashboardId({
          variables: {
            id: customer.id,
            metabaseDashboardId: values.metabaseDashboardId.trim(),
          },
        })
        return data
          ? {
              metabaseDashboardId:
                data.setTenantMetabaseDashboardId.metabaseDashboardId ?? '',
            }
          : undefined
      },
    })

  return (
    <SectionCard
      // The dashboard ID has never had a way to be cleared, and the mutation
      // takes a non-null String, so an empty field is not a saveable state.
      canSave={values.metabaseDashboardId.trim() !== ''}
      isDirty={isDirty}
      isLoading={isLoading}
      onDiscard={reset}
      onSave={submit}
      rows={[
        {
          content: (
            <Input
              disabled={isLoading}
              style={{ maxWidth: 260 }}
              value={values.metabaseDashboardId}
              onChange={event =>
                updateValues({ metabaseDashboardId: event.target.value })
              }
            />
          ),
          label: t('customerDetails.fields.metabaseDashboardId'),
        },
      ]}
      title={t('customerDetails.sections.reporting')}
    />
  )
}

export default ReportingSection
